import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chain } from '@incpt/shared';
import { BottomSheet } from './BottomSheet';
import { ChevronRight } from './icons';
import { coinKey, type Coin } from './TokenSheet';
import { TrxIcon } from '../icons/TrxIcon';
import { EthIcon } from '../icons/EthIcon';
import { BnbIcon } from '../icons/BnbIcon';
import { SolIcon } from '../icons/SolIcon';
import { HypeIcon } from '../icons/HypeIcon';
import { TonIcon } from '../icons/TonIcon';
import { BtcIcon } from '../icons/BtcIcon';
import { ZecIcon } from '../icons/ZecIcon';
import { XmrIcon } from '../icons/XmrIcon';
import { DogeIcon } from '../icons/DogeIcon';
import { XrpIcon } from '../icons/XrpIcon';

type Mark = typeof TrxIcon;

/** Chain mark drawn on the token icon. Used wherever a multi-chain symbol
 *  (USDT, USDC) would otherwise be the same green or blue circle. */
export const NETWORK_ICON: Record<Chain, Mark> = {
  eth: EthIcon,
  trx: TrxIcon,
  bsc: BnbIcon,
  sol: SolIcon,
  hyperevm: HypeIcon,
  ton: TonIcon,
  btc: BtcIcon,
  zec: ZecIcon,
  xmr: XmrIcon,
  doge: DogeIcon,
  xrp: XrpIcon,
};

type Props = {
  coins: Coin[];
  value: Coin['chain'];
  onChange: (chain: Coin['chain']) => void;
  /** Extra classes on the trigger (placement / alignment). */
  className?: string;
};

/** Compact network picker — one label + sheet. Replaces the pill row that
 *  overflowed once a symbol had many chains (USDT / USDC). */
export function NetworkSelect({ coins, value, onChange, className }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (coins.length <= 1) return null;

  const selected = coins.find((c) => c.chain === value) ?? coins[0]!;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          'flex items-center gap-1 self-start rounded-full bg-black/[0.06] py-1.5 pl-3 pr-2 active:opacity-70'
        }
      >
        <span className="text-[13px] font-medium text-ink">{selected.network}</span>
        <ChevronRight size={14} className="rotate-90 text-muted" />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={t('coin.network')}>
        <div className="mt-4 overflow-hidden rounded-[20px] bg-[#f7f7f8]">
          {coins.map((c, i) => {
            const active = c.chain === value;
            const NetworkIcon = NETWORK_ICON[c.chain];
            return (
              <button
                key={coinKey(c)}
                type="button"
                onClick={() => {
                  onChange(c.chain);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.03] ${
                  i > 0 ? 'border-t border-black/[0.06]' : ''
                }`}
              >
                <span className="relative shrink-0">
                  <c.Icon size={44} />
                  <span
                    className="pointer-events-none absolute bottom-0 right-0 flex rounded-full bg-[#f7f7f8] p-0.5"
                    aria-hidden
                  >
                    <NetworkIcon size={18} className="block" />
                  </span>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-medium text-ink">{c.network}</div>
                  <div className="text-[13px] text-muted">{c.symbol}</div>
                </div>
                {active && (
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-[#377dff] text-white"
                    aria-hidden
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6.2 5 8.5 9.5 3.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
